import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Package, AlertTriangle, TrendingDown, DollarSign, Upload, Sparkles, CheckCircle2, XCircle, Clock, Database } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [dataCoverage, setDataCoverage] = useState(null);
  const [aiInsights, setAiInsights] = useState('');
  const [loadingInsights, setLoadingInsights] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchStats();
    fetchDataCoverage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load dashboard stats',
        variant: 'destructive',
      });
    }
  };

  const fetchDataCoverage = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/data-coverage`);
      setDataCoverage(response.data);
    } catch (error) {
      console.error('Failed to load data coverage:', error);
    }
  };

  const fetchAiInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await axios.post(`${API}/forecast/ai-insights`);
      setAiInsights(response.data.insights);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load AI insights',
        variant: 'destructive',
      });
    } finally {
      setLoadingInsights(false);
    }
  };

  if (!stats) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </Layout>
    );
  }

  const statCards = [
    {
      title: 'Total Products',
      value: stats.total_products,
      icon: Package,
      color: 'text-foreground',
      testId: 'stat-total-products'
    },
    {
      title: 'Low Stock Items',
      value: stats.low_stock_count,
      icon: AlertTriangle,
      color: 'text-warning',
      testId: 'stat-low-stock'
    },
    {
      title: 'Reorder Needed',
      value: stats.reorder_needed_count,
      icon: TrendingDown,
      color: 'text-destructive',
      testId: 'stat-reorder-needed'
    },
    {
      title: 'Inventory Value',
      value: `₹${stats.total_inventory_value.toLocaleString()}`,
      icon: DollarSign,
      color: 'text-success',
      testId: 'stat-inventory-value'
    },
  ];

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">Dashboard</h1>
          <p className="text-muted-foreground mt-2">Overview of your inventory operations</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.title}
                data-testid={stat.testId}
                className="border border-border bg-card rounded-sm p-6 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                    {stat.title}
                  </p>
                  <Icon className={`w-4 h-4 ${stat.color}`} strokeWidth={1.5} />
                </div>
                <p className="text-3xl font-bold tracking-tight font-mono">{stat.value}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Data Coverage Section */}
          <div className="border border-border bg-card rounded-sm p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold tracking-tight font-heading" data-testid="data-coverage-title">
                <Database className="w-4 h-4 inline-block mr-2 text-muted-foreground" strokeWidth={1.5} />
                Data Coverage for Forecasting
              </h2>
              {dataCoverage && (
                <div className="flex gap-4 text-xs">
                  <span className={dataCoverage.summary.fba_platforms_ok === dataCoverage.summary.fba_platforms_total ? 'text-success' : 'text-warning'}>
                    FBA: {dataCoverage.summary.fba_forecast_ready}
                  </span>
                  <span className={dataCoverage.summary.reorder_platforms_ok === dataCoverage.summary.reorder_platforms_total ? 'text-success' : 'text-warning'}>
                    Reorder: {dataCoverage.summary.reorder_ready}
                  </span>
                </div>
              )}
            </div>
            
            {dataCoverage ? (
              <>
                {/* Recommendations */}
                {dataCoverage.recommendations && dataCoverage.recommendations.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {dataCoverage.recommendations.map((rec, idx) => (
                      <div 
                        key={`rec-${rec.type}-${idx}`}
                        className={`flex items-start gap-2 p-3 rounded-sm ${
                          rec.type === 'success' ? 'bg-success/10 text-success' :
                          rec.type === 'warning' ? 'bg-warning/10 text-warning' :
                          'bg-blue-500/10 text-blue-600'
                        }`}
                      >
                        {rec.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
                         rec.type === 'warning' ? <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
                         <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                        <div>
                          <p className="text-sm font-medium">{rec.message}</p>
                          <p className="text-xs opacity-80 mt-0.5">{rec.action}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Platform Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {dataCoverage.platforms.map((platform) => (
                    <div 
                      key={platform.platform_id}
                      data-testid={`coverage-${platform.platform_id}`}
                      className={`p-3 rounded-sm border ${
                        platform.status === 'ok' ? 'border-success/30 bg-success/5' :
                        platform.status === 'stale' ? 'border-warning/30 bg-warning/5' :
                        'border-destructive/30 bg-destructive/5'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{platform.platform_name}</span>
                        {platform.status === 'ok' ? (
                          <CheckCircle2 className="w-4 h-4 text-success" />
                        ) : platform.status === 'stale' ? (
                          <Clock className="w-4 h-4 text-warning" />
                        ) : (
                          <XCircle className="w-4 h-4 text-destructive" />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        {platform.status === 'missing' ? (
                          <p>No data uploaded</p>
                        ) : (
                          <>
                            <p>30d: {platform.units_30d.toLocaleString()} units | 90d: {platform.units_90d.toLocaleString()} units</p>
                            <p>{platform.date_range_start} to {platform.date_range_end}</p>
                          </>
                        )}
                        {platform.is_fba_platform && (
                          <span className="inline-block mt-1 px-1.5 py-0.5 bg-blue-500/10 text-blue-600 rounded text-[10px]">
                            FBA
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Loading data coverage...</p>
            )}
          </div>

          <div className="border border-border bg-card rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold tracking-tight font-heading" data-testid="upload-history-title">Upload History</h2>
              <Upload className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {stats.upload_history && stats.upload_history.length > 0 ? (
                stats.upload_history.map((upload, idx) => (
                  <div key={`upload-${upload.platform}-${idx}`} className="py-2 px-3 bg-muted/30 rounded-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium font-mono">{upload.platform}</span>
                      <span className="text-xs text-muted-foreground">
                        {upload.records_count} records
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {upload.filename}
                    </p>
                    {upload.date_range_start && upload.date_range_end && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Data: {new Date(upload.date_range_start).toLocaleDateString()} to {new Date(upload.date_range_end).toLocaleDateString()}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Uploaded: {new Date(upload.uploaded_at).toLocaleString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No upload history available</p>
              )}
            </div>
          </div>

          <div className="border border-border bg-card rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold tracking-tight font-heading" data-testid="ai-insights-title">AI Insights</h2>
              <Sparkles className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            </div>
            {!aiInsights ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Get AI-powered insights about your sales trends and inventory patterns.
                </p>
                <button
                  onClick={fetchAiInsights}
                  data-testid="generate-insights-button"
                  disabled={loadingInsights}
                  className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-sm text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {loadingInsights ? 'Analyzing...' : 'Generate Insights'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-sm p-4">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="ai-insights-content">{aiInsights}</p>
                </div>
                <button
                  onClick={fetchAiInsights}
                  data-testid="refresh-insights-button"
                  disabled={loadingInsights}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Refresh Insights
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}